import { useState } from "react";
import { motion } from "framer-motion";
import * as Popover from "@radix-ui/react-popover";
import {
  CheckCircle2,
  Circle,
  MessageCircle,
  Lock,
  Clock,
  Plus,
  Loader2,
  FolderOpen,
  ListChecks,
  CalendarClock,
} from "lucide-react";
import { Badge, springs } from "@/components/ui";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import { useTranslation } from "@audit-app/utils";
import { FileIcon } from "./FileIcon";
import { getCategoryStyle, statusToBadge, statusLabel } from "./constants";
import { getCategoryLucideIcon } from "./category-icon-map";
import type { DocumentView, DocumentChecklistItem } from "./types";

interface DocumentGridCardProps {
  doc: DocumentView;
  index: number;
  onOpen: (id: string) => void;
  onTagClick?: (docId: string) => void;
}

export function DocumentGridCard({
  doc,
  index,
  onOpen,
  onTagClick,
}: DocumentGridCardProps) {
  const { t } = useTranslation(["documents"]);
  const [isHovered, setIsHovered] = useState(false);

  const checklist = doc.checklist;
  const hasChecklist = checklist && checklist.length > 0;
  const doneCount = hasChecklist ? checklist.filter((c) => c.done).length : 0;
  const totalCount = hasChecklist ? checklist.length : 0;
  const progress = hasChecklist ? (doneCount / totalCount) * 100 : 0;

  const author = doc.author ?? {
    name: t("documents:documentGridCard.unknown"),
  };
  const members = doc.collaborators ?? [author];
  const catStyle = getCategoryStyle();
  const CategoryIcon =
    getCategoryLucideIcon(doc.categoryIcon ?? "") ?? FolderOpen;
  const categoryName =
    doc.categoryName ?? t("documents:documentGridCard.unknown");

  return (
    <motion.div
      role="button"
      tabIndex={0}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      transition={{
        ...springs.smooth,
        delay: Math.min(index, 16) * 0.03,
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onOpen(doc.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(doc.id);
        }
      }}
      className="group relative flex flex-col items-stretch rounded-card bg-card text-left cursor-pointer overflow-clip border border-transparent hover:border-border/60"
      style={{ padding: 0 }}
    >
      {/* Accent left bar — slides in on hover */}
      <motion.div
        className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent rounded-l-card"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: isHovered ? 1 : 0 }}
        transition={springs.snappy}
        style={{ originY: 0.5 }}
      />

      {/* Hover glow */}
      <motion.div
        className="absolute inset-0 rounded-card pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        style={{
          boxShadow:
            "0 4px 24px -4px rgba(232, 134, 42, 0.1), 0 2px 8px -2px rgba(0,0,0,0.06)",
        }}
      />

      <div className="p-card flex flex-col flex-1">
        {/* Top row: category + status + confidential */}
        <div className="flex items-center gap-1.5 mb-3">
          <span
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium"
            style={{
              backgroundColor: catStyle.bg,
              color: catStyle.text,
            }}
          >
            <CategoryIcon className="h-3 w-3" />
            {categoryName}
          </span>
          <Badge variant="status" status={statusToBadge[doc.status]}>
            {doc.status === "under-review" && (
              <motion.span
                className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-1 align-middle"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              />
            )}
            {statusLabel[doc.status]}
          </Badge>
          {doc.isProcessing && (
            <span className="flex items-center gap-1 text-[10px] text-accent font-medium">
              <Loader2 size={11} className="animate-spin" />
              {t("documents:documentGridCard.processing")}
            </span>
          )}
          {doc.confidential && (
            <Lock size={12} className="ml-auto text-muted shrink-0" />
          )}
        </div>

        {/* File icon + title */}
        <div className="flex items-center gap-3 mb-1.5">
          <motion.div
            animate={{ rotate: isHovered ? [0, -6, 6, 0] : 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <FileIcon type={doc.type} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-primary truncate">
              {doc.title}
            </p>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-1 mb-2">
          {doc.tags?.map((tag) => (
            <TagBadge
              key={tag.id}
              name={tag.name}
              color={tag.color}
              onClick={() => {
                onTagClick?.(doc.id);
              }}
            />
          ))}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTagClick?.(doc.id);
            }}
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-muted hover:text-secondary border border-dashed border-border hover:border-muted transition-colors"
          >
            <Plus className="h-3 w-3" />
            {!doc.tags?.length && t("documents:documentGridCard.addTag")}
          </button>
        </div>

        {/* Description */}
        {doc.description && (
          <p className="text-xs text-secondary leading-relaxed line-clamp-2 mb-2">
            {doc.description}
          </p>
        )}

        {/* Metadata row */}
        <div className="flex items-center gap-2 text-[11px] text-muted mb-3 flex-wrap">
          <span className="flex items-center gap-1">
            <Clock size={10} className="shrink-0" />
            {doc.updatedAt}
          </span>

          {doc.version && (
            <>
              <span className="text-border">|</span>
              <span className="font-medium text-secondary">{doc.version}</span>
            </>
          )}

          {doc.expirationDateTimestamp &&
            (() => {
              const now = Date.now();
              const daysUntil = Math.ceil(
                (doc.expirationDateTimestamp - now) / (1000 * 60 * 60 * 24),
              );
              let colorClass: string;
              if (daysUntil <= 7) {
                colorClass = "text-red-400 bg-red-500/10";
              } else if (daysUntil <= 30) {
                colorClass = "text-amber-400 bg-amber-500/10";
              } else {
                colorClass = "text-muted bg-card";
              }
              const label =
                daysUntil <= 0
                  ? t("documents:documentGridCard.expired")
                  : daysUntil === 1
                    ? t("documents:documentGridCard.dayLeft")
                    : t("documents:documentGridCard.daysLeft", {
                        days: daysUntil,
                      });
              return (
                <>
                  <span className="text-border">|</span>
                  <span
                    className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${colorClass}`}
                  >
                    <CalendarClock size={9} />
                    {label}
                  </span>
                </>
              );
            })()}
        </div>

        {/* Checklist section with popover */}
        {hasChecklist && (
          <ChecklistPopoverSection
            checklist={checklist}
            doneCount={doneCount}
            totalCount={totalCount}
            progress={progress}
            index={index}
            t={t}
          />
        )}

        {/* Divider */}
        <div className="border-t border-border mt-auto mb-2.5 pt-2" />

        {/* Footer: author + avatars + comment count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            {/* Overlapping avatars with hover expansion */}
            <motion.div
              className="flex shrink-0"
              initial="collapsed"
              whileHover="expanded"
            >
              {members.map((member, i) =>
                member.avatarUrl ? (
                  <motion.img
                    key={member.name}
                    src={member.avatarUrl}
                    alt={member.name}
                    title={member.name}
                    className="h-6 w-6 rounded-full border-2 border-card object-cover shrink-0"
                    variants={{
                      collapsed: { marginLeft: i === 0 ? 0 : -8 },
                      expanded: { marginLeft: i === 0 ? 0 : 2 },
                    }}
                    transition={springs.snappy}
                  />
                ) : (
                  <motion.div
                    key={member.name}
                    className="h-6 w-6 rounded-full border-2 border-card flex items-center justify-center text-[9px] font-medium text-white shrink-0"
                    style={{
                      backgroundColor: member.avatarColor ?? "#6b7280", // Default gray color if no avatarColor
                    }}
                    title={member.name}
                    variants={{
                      collapsed: { marginLeft: i === 0 ? 0 : -8 },
                      expanded: { marginLeft: i === 0 ? 0 : 2 },
                    }}
                    transition={springs.snappy}
                  >
                    {member.name.charAt(0).toUpperCase()}
                  </motion.div>
                ),
              )}
            </motion.div>
            {/* Author name */}
            <span className="text-[11px] text-muted truncate">
              {author.name.split(" ")[0]}
            </span>
          </div>

          {/* Comment count */}
          {doc.commentCount != null && doc.commentCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-secondary">
              <MessageCircle className="h-3.5 w-3.5" />
              {doc.commentCount}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Checklist Popover Section ───────────────────────────────────────────────

function getProgressColor(progress: number) {
  if (progress === 100) return "#22c55e";
  if (progress >= 50) return "#E8862A";
  return "#a1a1a6";
}

function ChecklistPopoverSection({
  checklist,
  doneCount,
  totalCount,
  progress,
  index,
  t,
}: {
  checklist: DocumentChecklistItem[];
  doneCount: number;
  totalCount: number;
  progress: number;
  index: number;
  t: import("i18next").TFunction<["documents"]>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSectionHovered, setIsSectionHovered] = useState(false);
  const previewItems = checklist.slice(0, 2);
  const progressColor = getProgressColor(progress);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <motion.div
          role="button"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          onHoverStart={() => setIsSectionHovered(true)}
          onHoverEnd={() => setIsSectionHovered(false)}
          className="relative -mx-2 rounded-lg px-2 cursor-pointer transition-colors"
          animate={{
            backgroundColor: isSectionHovered
              ? "rgba(232, 134, 42, 0.06)"
              : "rgba(0,0,0,0)",
          }}
          transition={{ duration: 0.15 }}
        >
          {/* Subtle left accent on hover */}
          <motion.div
            className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full"
            style={{ backgroundColor: progressColor }}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{
              scaleY: isSectionHovered ? 1 : 0,
              opacity: isSectionHovered ? 1 : 0,
            }}
            transition={springs.snappy}
          />

          <div className="border-t border-border mb-2.5" />

          {/* Header row */}
          <div className="flex items-center gap-2 mb-2">
            <motion.div
              animate={{ rotate: isSectionHovered ? [0, -8, 8, 0] : 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
            >
              <ListChecks size={12} className="text-muted shrink-0" />
            </motion.div>
            <span className="text-[10px] font-medium text-muted uppercase tracking-wide">
              {t("documents:documentGridCard.checklist")}
            </span>
            <span className="text-[10px] text-secondary font-medium">
              {doneCount}/{totalCount}
            </span>
            <div className="flex-1 h-1 bg-border/50 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{
                  ...springs.smooth,
                  delay: Math.min(index, 16) * 0.03 + 0.3,
                }}
                style={{ backgroundColor: progressColor }}
              />
            </div>
          </div>

          {/* Preview items */}
          <div className="space-y-0.5 pb-1">
            {previewItems.map((item) => (
              <div key={item.id} className="flex items-center gap-1.5">
                {item.done ? (
                  <CheckCircle2
                    size={10}
                    className="text-emerald-500 shrink-0"
                  />
                ) : (
                  <Circle size={10} className="text-muted/60 shrink-0" />
                )}
                <span
                  className={`text-[10px] leading-tight truncate ${
                    item.done ? "line-through text-muted" : "text-secondary"
                  }`}
                >
                  {item.label}
                </span>
              </div>
            ))}
            {checklist.length > 2 && (
              <motion.span
                className="text-[10px] text-muted pl-4"
                animate={{ opacity: isSectionHovered ? 1 : 0.6 }}
              >
                {t("documents:documentGridCard.moreItems", {
                  count: checklist.length - 2,
                })}
              </motion.span>
            )}
          </div>
        </motion.div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={16}
          className="z-50 w-[300px] overflow-hidden rounded-[10px] bg-card border border-border shadow-xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
        >
          {/* Accent bar */}
          <div className="h-[3px]" style={{ backgroundColor: progressColor }} />

          <div className="p-3">
            {/* Popover header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ListChecks size={14} className="text-secondary" />
                <span className="text-[12px] font-semibold text-primary">
                  {t("documents:documentGridCard.checklist")}
                </span>
              </div>
              <span
                className="text-[11px] font-medium px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${progressColor}18`,
                  color: progressColor,
                }}
              >
                {doneCount}/{totalCount}
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-border/50 rounded-full overflow-hidden mb-3">
              <motion.div
                className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={springs.smooth}
                style={{ backgroundColor: progressColor }}
              />
            </div>

            {/* Item list */}
            <div className="space-y-0.5 max-h-[240px] overflow-y-auto">
              {checklist.map((item, i) => (
                <motion.button
                  key={item.id}
                  type="button"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-left transition-colors"
                >
                  {item.done ? (
                    <CheckCircle2
                      size={14}
                      className="text-emerald-500 shrink-0"
                    />
                  ) : (
                    <Circle size={14} className="text-muted/50 shrink-0" />
                  )}
                  <span
                    className={`text-[12px] leading-snug flex-1 ${
                      item.done ? "line-through text-muted" : "text-primary"
                    }`}
                  >
                    {item.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          <Popover.Arrow className="fill-card" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
